<template>
  <div>

    <div class="title-section">
      <div class="display-1 font-weight-bold">{{meta.title}}</div>
      <div class="subheading">{{meta.subtitle}}</div>
    </div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Preface</div><v-divider class="div-after"/>
    <div class="body-1">
      This checkpoint revolves around setting up a framework for the ray tracer! All that
      needs to be delivered is the resultant image, but I'll be going into a little more
      depth here. 
    </div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Current Status</div><v-divider class="div-after"/>
    <v-img class="u-image" :src="require(`../assets/updates/3/basicrender.png`)"/>
      <div class="font-weight-medium">An image rendered using the current system</div>
    <div class="body-1">
      Great, we've rendered an image that kind of resembles
      <span class="font-italic">Whitted's two-spheres-and-a-checkerboard-plane image!</span> 
      But there's more to talk about...
    </div>
    <card-list :items="topic_list"/>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Current Application</div><v-divider class="div-after"/>
    <div class="body-1">
      For this checkpoint, I wanted to focus primarily on the design of the raytacing framework,
      and I explicitly did not want to get caught up in <span class="font-weight-bold">gold plating</span>. 
      I've accomplished this goal, but of course it comes with a trade-off in the short term. 
      The following diagram doesn't offer much beyond taking a high-level snapshot of where things are, and 
      what can be improved, so don't look at it too hard! 
    </div>
    <v-img class="u-image-large" :src="require(`../assets/updates/3/status_diagram.png`)"/>
      <div class="font-weight-medium">Breakdown of app-level work</div>
    <div class="body-1">
      There's a bit to unravel here (not all of it is immediately important) and... it gets the job done!
      We'll talk about the ray tracing stuff next, but for now let's look at how it's used in the app. 
      We've got one class that actually displays the image, and it offers some input fields for manipulating
      a static scene with two spheres and a plane (composed of two triangles). It's not pretty, it's not 
      even close to "extensible", and luckily it's scrapwork :)
    </div>
    <v-img class="u-image" :src="require(`../assets/updates/3/side_view.png`)"/>
      <div class="font-weight-medium">Side view, created quickly with basic UI controls</div>
    <v-img class="u-image" :src="require(`../assets/updates/3/top_down.png`)"/>
      <div class="font-weight-medium">And top-down!</div>
    <div class="body-1">
      It makes sense getting the raytracing framework up and running prior to any pretty-pretty-UI work, 
      though I'd like to have a better long-term solution that can allow for easier testing of the renderer
      itself. I'll need to balance the complexity/completeness of the UI with the progress that I make 
      on the raytracer itself, as going too far in either direction is going to hurt the efficiency with which 
      I'm able to complete upcoming checkpoints.
    </div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Raytracing Framework</div><v-divider class="div-after"/>
    <div class="body-1">
      My approach here was to come up with a design that would easily allow for me to plug
      and play different milestones of the project once I've completed it. As a result, 
      I placed emphasis on (1) designing cohesive classes that follow SRP as closely as possible
      and (2) breaking down the system into abstract parts and understanding what interfaces
      I would need to transfer data between them, no matter how complex the render actually gets. 
    </div>
    <div class="body-1">
      While the diagram does not include any implementations, relationships between interfaces 
      and responsibilities certainly are captured. Anything in <span style="color: #f4c242">orange</span> 
      notes something that is currently missing but has been considered. Anything in
      <span style="color: #f44141">red</span> notes something that is likely to change!
    </div>
    <v-img class="u-image-large" :src="require(`../assets/updates/3/raytracer_framework.png`)"/>
      <div class="font-weight-medium">Class diagram for ray tracing framework</div>
    <div class="body-1">
      That's a lot, and every bit of it has plenty of thought put into it! Let's start by looking
      at the higher level use of this framework. 
    </div>
    <div class="headline font-weight-bold">Using the Ray-Tracer</div>
    <v-img class="u-image" :src="require(`../assets/updates/3/class_raytracer.png`)"/>
      <div class="font-weight-medium">Some important classes</div>
    <div class="body-1">
      The RayTracer class is designed with two significant responsibilities. The first is 
      to actually populate the pixels of the Image that we're rendering (called LiveImage here).
      The second is actually determining the color for a given pixel of the image. More 
      specifically, this class will <span class="font-weight-bold">spawn rays, shoot them
      into the scene and use the result to determine the color of a given pixel</span>
    </div>
    <div class="body-1">
      This class is explicitly decoupled from traversing the Scene, as the Scene itself 
      maintains those Entities and should be responsible for that! The RayTracer doesn't
      care about <span class="font-italic">how it figures out if we've hit an Entity.</span>
      So we can make this class super-super focused on responding to hits and misses!
      We can also entirely decouple this class from the definition of an Entity, so long
      as we have some way of understanding the "material" (hint hint), but I'll figure that 
      out after I understand how I'll be defining the materials of entities in this system.
    </div>
    <div class="body-1">
      Now, there's plenty more we can talk about here, but those are the super important
      bits. Post-processing should be fairly simple to fit into this design, as the interface 
      doesn't explicitly exclude that step. Multi-threading is also pretty easy to incorporate
      here, as we could spin off a series of worker-threads focused on a subset of pixels.
      Oh, and this is agnostic of any framework for rendering the image; LiveImage could easily 
      be extended with a specific library or class in mind. 
    </div>
    <div class="body-1">
      On a side note - I'm thinking about removing the ImageExporter interface from the framework 
      package, as it really feels like more of an external client of the framework/subsystem. 
    </div>
    <div class="headline font-weight-bold">RayTracer, Scene and Hit</div>
    <v-img class="u-image" :src="require(`../assets/updates/3/raytracer_and_scene.png`)"/>
      <div class="font-weight-medium">RayTracer/Scene/Hit</div>
    <div class="body-1">
      Looking inward, the two interfaces that the RayTracer should ultimately interact with 
      include the Scene and Hit interfaces. There's a useful "spawnRay" method that allows us
      to spawn a ray from the origin of the camera (which we'll assume is always be 0,0,0 since
      we're tracing in view-space), but otherwise the primary interaction between the RayTracer 
      and the scene is through the "shootIntoScene" method. We'll pass a ray into the Scene, and
      we'll get some information about the closest entity that we've intersected in the form of
      a <span class="font-weight-bold">Hit</span>. I haven't fully decided what I want to include
      in this class just yet, though ideally it'd only include a description of the 
      <span class="font-weight-bold">Material</span>, the original <span class="font-weight-bold">Ray</span>
      and the <span class="font-weight-bold">Point</span> along the ray where we intersected the 
      entity. This should cover most cases where we need to decide what (if any) rays we need to
      spawn after impact. 
    </div>
    <div class="headline font-weight-bold">Scene/Entity Related Classes</div>
    <v-img class="u-image" :src="require(`../assets/updates/3/scene_entity.png`)"/>
      <div class="font-weight-medium">Scene, Camera, Entity, Spatial Structure</div>
    <div class="body-1">
      The <span class="font-weight-bold">Scene</span> interface plays the role of a Facade for
      the rest of the classes/functionality, and maintains all scene entities (including unseen
      entities, such as the <span class="font-weight-bold">Camera</span>). The responsibility for 
      traversing the Scene is handed off to the <span class="font-weight-bold">Spatial Structure</span>,
      which is purposefully named to imply the use of a spatial data structure. This design 
      doesn't necessarily dictate <span class="font-italic">how Entities are stored</span>, but 
      it's definitely an important memory concern to keep in mind. 
    </div>
    <div class="body-1">
      Of course, the orange implies that <span class="font-italic">none of this really exists</span>
      just yet, but it wasn't really needed for the initial scene given it's complexity. Similarly,
      only one camera was used for the initial scene. Speaking of, the <span class="font-weight-bold">
      Camera</span> offers some helpful methods for calculating the view matrix and spawning rays
      from its origin according to some interpolated u/v position (where 0,0 maps to the top left
      of the Camera's virtual film plane and 1,1 maps to the bottom right). An interesting detail to
      note here is that the Camera is decoupled from the ray tracing algorithm. I am considering
      adjusting this relationship and having the "ray tracer" be more of a <span class="font-italic">
      strategy</span> that is used when telling the camera to capture the scene. 
    </div>
    <div class="body-1">
      Finally, the <span class="font-weight-bold">Entity</span> simply represents an object in the
      scene that may or may not be visible. The Entity itself will ultimately receive a Ray and
      determine whether or not said Ray intersects the Entity, returning either a Hit containing
      information about that intersection or null if no intersection occurs. 
    </div>
    <div class="headline font-weight-bold">Summary</div>
    <div class="body-1">
      The high level interfaces that act as the foundation of this framework have enough definition
      to suggest general use, while enabling hot-swapping of various components. This is going to be
      essential as my project evolves in complexity, especially if I want to evaluate various stages
      of the project without restarting or reconfiguring my project. 
    </div>
    <div class="body-1">
      This design has been very useful for me in the early stages of development, as well! Getting 
      up and running with more focused classes that rely on interfaces allows for me to easily complete
      the high level app flow before actually digging into the nitty-gritty, which is something that
      I like to leave out until I really need it. 
    </div>
    <div class="headline font-weight-bold">Note - Hardware Acceleration</div>
    <div class="body-1">
      The raytracing framework serves two purposes - the first is to define the structure of
      our scene, and the second is to enable CPU-based rendering. This comes out of a decision
      to follow the information expert principle and keep out data closely bound to any behavior
      that acts on it (specifically for the CPU renderer). We don't necessarily need to leverage 
      all of the features of this framework for a hardware accelerated renderer, and in fact 
      much of this is going to be re-hashed with OpenCL (intersections, rays, spatial data structure
      creation and traversal, etc...)
    </div>
    <div class="body-1">
      There certainly is a loss of time/effort due to the need to reimplement many features 
      by supporting both software and hardware based rendering, but this design best balances 
      the needs of both.
    </div>
    <div class="headline font-weight-bold">Missing Features / Out-of-scope</div>
    <card-list :items="missing_list"/>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Improvements</div><v-divider class="div-after"/>
    <card-list :items="improvements"/>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Long Term Goals</div><v-divider class="div-after"/>
    <div class="body-1">
      I'm getting a better idea of where the project can go from here! My major goals remain 
      the same, and I've got a much clearer picture about how I'll accomplish them. 
    </div>
    <div class="body-1">
      My next steps are going to be addressing the improvements mentioned in this update, and 
      switching my focus back to high-level design of the application. In terms of expanding the
      capabilities of the system, I'm looking into implementing <span class="font-weight-bold">Phong
      illumination</span> and support for <span class="font-weight-bold">K-D Trees</span>! I'd like
      to expand on the illumination side of things and experiment with different models. Cook-Torrance
      seems like a nifty challenge, though I'll have to nail down material descriptions prior to
      
    </div>

  </div>
</template>

<script>
import CardList from '../components/CardList';

export default {
  name: 'U_3',
  components: { CardList, },
  data() {
    return {
      topic_list: [
        {title: 'Current Application', desc: '"Gets the job done"', },
        {title: 'Raytracing Framework', desc: 'More emphasis placed here in terms of design', },
        {title: 'Improvements', desc: 'Some shorter term improvements to be made', },
        {title: 'Long Term Goals', desc: 'What do I want to accomplish next?', },
      ],
      missing_list: [
        {title: 'Scene Loading', desc: 'Not implemented just yet, nor is it a concern of this subsystem.', },
        {title: 'Scene Conversion', desc: 'Conversion to different formats (ie: OpenCL) isn\' supported just yet! Probably out-of-scope here, too', },
        {title: 'Anything in Orange', desc: 'In the diagrams, specifically', },
      ],
      improvements: [ //{title: '', desc: '', },
        {title: 'Facade', desc: 'The UI doesn\'t need to be coupled directly with the ray tracing framework, ' +
          'and a high level facade could act as a useful interface between it and the UI. The only ' + 
          'coupling that needs to happen is between the UI and the "LiveImage" class, so that implies ' +
          'that the class should probably be pulled out to a higher level (perhaps outside of the RT framework).', },
        {title: 'Naming', desc: 'There are some... interesting names for classes. LiveImage and Raytracer feel ' +
          'a little silly. I think RT needs to be replaced by something else that better captures ' +
          'the responsibilities of the class. It renders an image using a scene, and it\'s also responsible ' +
          'for traversing that scene. It\'s going to be the core of the system, no doubt, and that\'s where ' +
          'we\'ll see enhancements appearing!', },
        {title: 'Experiments', desc: 'These can probably be moved around', },
        {title: 'UI', desc: 'The current UI is very much temporary. It doesn\'t dynamically load ' +
          'based on the scene\'s objects, it\'s not too pretty. ', },
        {title: 'Performance', desc: 'Optimizations were not made at this point, in favor of achieving some other ' +
         'goals/quality attributes. Since I\'ve got a good idea of where I\'m headed in terms of design, ' +
         'this is going to take priority for future checkpoints!', },
      ]
    };
  },
  computed: {
    meta() {
      return this.$store.state.updates[3];
    },
  },
}
</script>

<style>

</style>
